<template>
  <div class="box" v-if="!showModelDetails">
    <div class="headline">
      <div class="top">Step 3 of 5</div>
      Apps and database models
    </div>
    <div v-if="recipe.auth">
      <h5>
        <small class="text-light bg-secondary rounded text-uppercase py-1 px-2 font-weight-bold" style="font-size: 0.5em; opacity: 0.5">app</small>
        users
      </h5>
      <ul class="models-list">
        <li>
          <code>users.User</code>
          <span class="docstring">User model</span>
        </li>
      </ul>
    </div>
    <div v-if="recipe.use_openapi">
      <h5>
        <small class="text-light bg-secondary rounded text-uppercase py-1 px-2 font-weight-bold" style="font-size: 0.5em; opacity: 0.5">app</small>
        openapi
      </h5>
      <small class="d-block mb-3">
        <i>Used for exposing OpenAPI/Swagger schema - no models</i>
      </small>
    </div>

    <div v-for="app in recipe.apps" :key="app.name" class="mt-2">
      <div v-if="editingApp !== app">
        <h5>
          <small class="text-light bg-secondary rounded text-uppercase py-1 px-2 font-weight-bold" style="font-size: 0.5em; opacity: 0.5">app</small>
          {{ app.name }}
          <small>
            <a href="#" @click.prevent="editApp(app)">(edit)</a>
          </small>
          <small v-if="!editingApp && !app.models.length">
            <a href="#" class="text-danger" @click.prevent="removeApp(app)">(remove)</a>
          </small>
        </h5>
      </div>
      <div v-else>

      <b-form-group
        class="required"
        :invalidFeedback="editingAppError"
        :state="editingAppError ? false : null"
      >
        <b-form-input
          v-model="editingAppName"
          placeholder="example: core"
          required
          @input="onAppNameEdit"
          @keyup="onAppNameKeyUp"
          :state="editingAppError ? false : null"
          ref="appNameInput"
        ></b-form-input>
      </b-form-group>
      </div>
      <div v-if="app.models.length > 0">
        <ul class="models-list mb-1">
          <li v-for="model in app.models" :key="model.name">
            <code>{{ app.name}}.{{ model.name }}</code>
            <span class="docstring">{{ model.docstring }}</span>
            <a v-if="!editingApp" href="#" @click.prevent="editModel(model, app)">(edit)</a>
          </li>
        </ul>
      </div>
      <div v-else :class="error ? 'text-danger text-center' : 'text-center'">
        App has no models defined.
      </div>
      <div class="text-right mb-2">
        <b-button size="sm" @click.prevent="editModel(null, app)" :disabled="!!editingApp">
          <font-awesome-icon icon="plus" />
          Add model
        </b-button>
      </div>
    </div>
    <div v-if="!this.recipe.apps.length" :class="error ? 'text-danger text-center' : 'text-center'">
      You have no apps defined. Create at least one app with at least one model.
    </div>
    <div>
      <b-button size="sm" @click.prevent="addApp()" :disabled="!!editingApp">
        <font-awesome-icon icon="plus" />
        Add app
      </b-button>
    </div>

    <b-form>
      <div class="button-row">
        <b-button @click="$emit('prev', recipe.apps)" :disabled="!!editingApp">&larr; Prev</b-button>
        <b-button variant="primary" @click="onNext" :disabled="!!editingApp">Next &rarr;</b-button>
      </div>
    </b-form>
  </div>
  <div v-else>
    <ModelDetail
      :recipe="recipe"
      :app="editedModelApp"
      :instance="editedModel"
      @create="onModelCreate"
      @update="onModelUpdate"
      @cancel="clearEditedModel"
      @remove="removeModel"
    />
  </div>
</template>

<script>
import Vue from 'vue'

import ModelDetail from './ModelDetail'
export default {
  name: 'ModelList',
  components: {ModelDetail},
  props: {
    recipe: {type: Object, required: true}
  },

  data () {
    return {
      error: false,
      showModelDetails: false,
      editedModel: null,
      editedModelApp: null,
      editingApp: null,
      editingAppName: null,
      editingAppError: null
    }
  },

  methods: {
    editApp (app) {
      this.editingApp = app
      this.editingAppName = app.name

      Vue.nextTick(
        () => this.$refs.appNameInput.length && this.$refs.appNameInput[0].$el.focus()
      )
    },

    onAppNameEdit (value) {
      this.editingAppError = ''

      value = value.trim()
      if (!value) {
        this.editingAppError = 'Each app must have a name'
        return
      }

      let otherAppNames = this.recipe.apps
        .filter(app => app !== this.editingApp)
        .map(app => app.name)
        .concat(['users', 'openapi'])

      if (otherAppNames.includes(value)) {
        this.editingAppError = 'App name must be unique'
        return
      }

      if (!value.match(/^[a-z][A-Za-z0-9]*$/)) {
        this.editingAppError = 'Must be a valid Django app name'
      }
    },

    onAppNameKeyUp (ev) {
      if (ev.key !== 'Enter' && ev.key !== 'Escape') {
        return
      }

      if (this.editingAppError) return

      const oldName = this.editingApp.name
      this.editingApp.name = this.editingAppName.trim()
      this.editingApp = null
      this.$emit('apprenamed', {oldName, newName: this.editingAppName})
    },

    editModel (model, app) {
      this.editedModel = model
      this.editedModelApp = app
      this.showModelDetails = true
    },

    clearEditedModel () {
      this.showModelDetails = false
      this.editedModel = null
    },

    addApp () {
      const app = {name: 'app', models: []}
      this.recipe.apps.push(app)
      this.editingApp = app
      this.editingAppName = 'app'

      Vue.nextTick(
        () => this.$refs.appNameInput.length && this.$refs.appNameInput[0].$el.focus()
      )
    },

    removeApp (app) {
      if (this.editingApp === app || app.models.length > 0) return
      const idx = this.recipe.apps.indexOf(app)
      if (idx === -1) return

      this.recipe.apps.splice(idx, 1)
    },

    onModelCreate (model) {
      this.editedModelApp.models.push(model)
      this.clearEditedModel()
    },

    onModelUpdate (model) {
      const models = this.editedModelApp.models

      const appName = this.editedModelApp.name
      const newName = `${appName}.${model.name}`
      const oldName = `${appName}.${this.editedModel.name}`

      const idx = models.indexOf(this.editedModel)
      models.splice(idx, 1, model)
      this.clearEditedModel()
      if (newName !== oldName) this.$emit('modelrenamed', {oldName, newName})
    },

    removeModel () {
      const models = this.editedModelApp.models
      const idx = models.indexOf(this.editedModel)
      models.splice(idx, 1)
      this.clearEditedModel()
    },

    validate () {
      if (!this.recipe.apps.length) {
        this.error = true
        return false
      }

      for (let app of this.recipe.apps) {
        if (!app.models.length) {
          this.error = true
          return false
        }
      }
      return true
    },

    onNext () {
      if (!this.validate()) return

      this.$emit('next', this.recipe.apps)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/wizard/ModelList.vue